import axios from 'axios'
import { getToken, removeToken } from './auth'
import { ElMessage } from 'element-plus'

// 创建axios实例
const api = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || '/api', // 使用代理路径
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
})

// 请求拦截器
api.interceptors.request.use(
    (config) => {
        // 添加认证token
        const token = getToken()
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }

        // 添加请求时间戳（避免缓存）
        if (config.method === 'get') {
            config.params = {
                ...config.params,
                _t: Date.now()
            }
        }

        console.log('🚀 发送请求:', config.method?.toUpperCase(), config.url)
        return config
    },
    (error) => {
        console.error('❌ 请求错误:', error)
        return Promise.reject(error)
    }
)

// 注意：响应拦截器在setupInterceptors中设置，避免重复设置

// 设置拦截器（供store使用）
export function setupInterceptors(store, router) {
    // 请求拦截器 - 添加Token和时间戳
    api.interceptors.request.use(
        async (config) => {
            // 添加认证token
            const token = getToken()
            if (token) {
                config.headers.Authorization = `Bearer ${token}`
            }

            // 添加请求时间戳（避免缓存）
            if (config.method === 'get') {
                config.params = {
                    ...config.params,
                    _t: Date.now()
                }
            }

            console.log('🚀 发送请求:', config.method?.toUpperCase(), config.url)
            return config
        },
        (error) => {
            console.error('❌ 请求错误:', error)
            return Promise.reject(error)
        }
    )

    // 响应拦截器 - 处理响应和错误
    api.interceptors.response.use(
        (response) => {
            console.log('✅ 收到响应:', response.config.url, response.status)
            // 统一返回 response.data，这样所有API调用都直接得到数据
            return response.data
        },
        async (error) => {
            console.error('❌ 响应错误:', error.response?.status, error.response?.data)

            if (error.response) {
                const { status, data } = error.response
                const errorCode = data?.error_code

                // 对于认证相关的特殊错误，不显示通用提示，让具体页面处理
                const authErrorCodes = [
                    'INVALID_CREDENTIALS', 'USER_NOT_FOUND', 'ACCOUNT_DISABLED',
                    'ACCOUNT_NOT_VERIFIED', 'ACCOUNT_LOCKED', 'TOO_MANY_ATTEMPTS',
                    'USERNAME_EXISTS', 'EMAIL_EXISTS', 'WEAK_PASSWORD'
                ]

                if (authErrorCodes.includes(errorCode)) {
                    // 认证相关错误不在这里显示，让调用方处理
                    return Promise.reject(error)
                }

                switch (status) {
                    case 401:
                        // 根据错误代码处理不同情况
                        if (errorCode === 'TOKEN_EXPIRED') {
                            ElMessage.error('登录已过期，请重新登录')
                            await store.dispatch('auth/logout')
                            router.push('/login')
                        } else if (errorCode === 'TOKEN_INVALID') {
                            ElMessage.error('登录状态无效，请重新登录')
                            await store.dispatch('auth/logout')
                            router.push('/login')
                        } else {
                            ElMessage.error(data?.message || '未授权访问')
                        }
                        break

                    case 403:
                        if (errorCode === 'ACCOUNT_DISABLED') {
                            ElMessage.error('账户已被禁用，请联系管理员')
                        } else if (errorCode === 'ACCOUNT_NOT_VERIFIED') {
                            ElMessage.error('账户未验证，请检查邮箱验证链接')
                        } else {
                            ElMessage.error('没有权限访问此资源')
                        }
                        break

                    case 404:
                        if (errorCode === 'USER_NOT_FOUND') {
                            // 用户不存在的错误让登录页面处理
                            return Promise.reject(error)
                        } else {
                            ElMessage.error('请求的资源不存在')
                        }
                        break

                    case 423:
                        ElMessage.error('账户暂时被锁定，请稍后再试')
                        break

                    case 429:
                        ElMessage.error('请求过于频繁，请稍后再试')
                        break

                    case 500:
                        ElMessage.error('服务器内部错误')
                        break

                    case 503:
                        ElMessage.error('服务暂时不可用，请稍后重试')
                        break

                    default:
                        ElMessage.error(data?.message || '请求失败')
                }
            } else if (error.request) {
                // 网络错误
                ElMessage.error('网络连接失败，请检查网络')
            } else {
                ElMessage.error('请求配置错误')
            }

            return Promise.reject(error)
        }
    )
}

// 认证相关API
export const authAPI = {
    // 用户登录
    login: (credentials) => api.post('/auth/login', credentials),

    // 用户注册
    register: (userData) => api.post('/auth/register', userData),

    // 忘记密码
    forgotPassword: (data) => api.post('/auth/forgot-password', data),

    // 验证Token - 修复：发送当前存储的token
    verifyToken: (token) => {
        const currentToken = token || getToken()
        return api.post('/auth/verify-token', { token: currentToken })
    },

    // 刷新Token
    refreshToken: () => api.post('/auth/refresh'),

    // 用户登出
    logout: () => api.post('/auth/logout'),

    // 获取可访问的系统
    getAvailableSystems: () => api.get('/systems'),

    // ==================== 手机号相关API ====================

    // 发送短信验证码
    sendSmsCode: (phone, purpose = 'login') => api.post('/sms/send-code', { phone, purpose }),

    // 手机号注册
    registerWithPhone: (userData) => api.post('/auth/register-phone', userData),

    // 短信验证码登录
    loginWithSms: (phone, verification_code, rememberMe = false) =>
        api.post('/auth/login-sms', { phone, verification_code, rememberMe }),

    // 重置密码
    resetPassword: (phone, verification_code, new_password) =>
        api.post('/auth/reset-password', { phone, verification_code, new_password }),

    // 账户登录（支持用户名或手机号）
    loginWithAccount: (account, password, rememberMe = false) =>
        api.post('/auth/login', { username: account, password, rememberMe })
}

// 用户相关API
export const userAPI = {
    // 获取用户资料
    getProfile: () => api.get('/user/profile'),

    // 更新用户资料
    updateProfile: (data) => api.put('/user/profile', data),

    // 修改密码
    changePassword: (data) => api.post('/user/change-password', data),

    // 获取用户偏好设置
    getPreferences: () => api.get('/user/preferences'),

    // 更新用户偏好设置
    updatePreferences: (data) => api.post('/user/preferences', data),

    // 获取用户活动记录
    getActivities: () => api.get('/user/activities')
}

// 系统相关API
export const systemAPI = {
    // 获取系统列表
    getSystems: () => api.get('/systems'),

    // 获取系统详情
    getSystemDetail: (systemId) => api.get(`/systems/${systemId}`),

    // 生成系统访问Token
    generateSystemToken: (systemId) => api.post(`/systems/${systemId}/token`)
}

// 通用API方法
export const commonAPI = {
    // 健康检查
    healthCheck: () => api.get('/health'),

    // 上传文件
    uploadFile: (file, onProgress) => {
        const formData = new FormData()
        formData.append('file', file)

        return api.post('/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
                if (onProgress) {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    )
                    onProgress(percentCompleted)
                }
            }
        })
    }
}

// 导出默认实例
export default api 